import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Styles from './mIndex.module.scss';
import { useDJIRC2InputFix } from '../../hooks';

export interface ICommonInput {
  id?: string;
  className?: string;
  label?: string;
  maxLength?: number;
  placeholder?: string;
  defaultValue?: string;
  value?: string;
  enableClear?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  required?: boolean;
  isNoInput?: boolean,
  errorMsg?: string;
  onChange?: (val?: string) => void;
  onFocus?: (val?: string) => void;
  onBlur?: (val?: string) => void;
  onDelete?: (val?: string) => void;
}

const CommonInput = ({
  className = '',
  label = '',
  defaultValue = '',
  value = '',
  disabled = false,
  enableClear = false,
  isNoInput = false,
  required = false,
  errorMsg = '',
  maxLength = 10000,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onDelete = () => {},
  ...restProps
}: ICommonInput) => {
  const [inputValue, setInputValue] = useState(defaultValue || value);
  const [minimumError, setMinimumError] = useState(false);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  
  // 使用 DJI RC 2 输入法修复 hooks
  const djiInputFix = useDJIRC2InputFix();

  useEffect(() => {
    setInputValue(value)
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onChange(inputValue);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNoInput) {
      e.preventDefault();
      return false;
    }
    
    let newValue = e.target.value;
    
    // DJI RC 2 设备特殊处理：检测并修复内容乱序
    djiInputFix.handleChange(e, (correctedValue) => {
      newValue = correctedValue;
      setInputValue(correctedValue);
    });
    
    setInputValue(newValue);
  };

  const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // DJI RC 2 设备特殊处理
    djiInputFix.handleKeyDown(e, inputValue, (correctedValue) => {
      setInputValue(correctedValue);
    });
    
    if ((e.key === 'Backspace' || e.keyCode === 8)) {
      onDelete();
    }
  };

  const handleFocus = () => {
    inputRef.current?.focus();
    setFocused(true);
    onFocus(inputValue);
  };

  const handleBlur = () => {
    inputRef.current?.blur();
    setFocused(false);
    setMinimumError(inputValue.length <= 0 && required);
    onBlur(inputValue);
    onChange(inputValue);
  };

  const handleClear = () => {
    setInputValue('');
  };

  // 处理输入法组合结束
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    djiInputFix.handleCompositionEnd(e);
    // 更新组件状态
    const correctedValue = e.currentTarget.value;
    setInputValue(correctedValue);
  };

  const handleErrorMsg = () => {
    if (inputValue.length >= maxLength) {
      return errorMsg;
    } else if (minimumError) {
      return `${label}字数不少于1个字`;
    }
  };

  return (
    <div className={cx(Styles.root, className, {
      [Styles.focused]: focused,
      [Styles.disabled]: disabled,
      [Styles.error]: !!errorMsg,
    })}>
      <input
        ref={inputRef}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeydown}
        onCompositionStart={djiInputFix.handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        disabled={disabled}
        value={inputValue}
        maxLength={maxLength}
        {...restProps}
      />
      {
        !!inputValue && enableClear && <div className={Styles.clear} onClick={handleClear} />
      }
      {
        <div className={Styles.error}>{handleErrorMsg()}</div>
      }
    </div>
  );
};

export default CommonInput;
